/** Translations of one object (collapsed; loads when opened): language picker, source beside each field, AI fill. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Collapse, Input, Select, Spin, Tooltip, Typography, message } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { AiCreditBadge, reportAiError, runAi } from './ai';
import { sitePath, toastErr, useLeaveGuard, useSite } from './common';

const { Text } = Typography;
export const langName = (code, ui) => {
    try { return new Intl.DisplayNames([ui || 'en'], { type: 'language' }).of(code) || code; } catch (_) { return code; }
};
const LONG = new Set(['description', 'body', 'excerpt', 'seo_description', 'bio']);

const Body = ({ type, oid }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { api, siteId } = useSite();
    const [data, setData] = useState(null);
    const [lang, setLang] = useState();
    const [draft, setDraft] = useState({}); // {lang: {field: text}}, unsaved changes only
    const [saving, setSaving] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);

    const load = useCallback(async () => {
        const d = await api.get(`/translations/${type}/${oid}`);
        setData(d);
        setLang((l) => (l && d.languages?.includes(l) ? l : d.languages?.[0]));
        return d;
    }, [api, type, oid]);
    useEffect(() => { load().catch((e) => toastErr(e)); }, [load]);

    const dirty = useMemo(() => Object.values(draft).some((f) => Object.keys(f).length), [draft]);
    useLeaveGuard(dirty);

    if (!data) return <div style={{ padding: 16, textAlign: 'center' }}><Spin size="small" /></div>;
    if (!data.languages?.length) {
        return <Text type="secondary">{t('No other languages yet.')} <a onClick={() => navigate(sitePath(siteId, 'settings/general'))}>{t('Add languages in settings')}</a></Text>;
    }
    const saved = data.values?.[lang] || {};
    const label = { title: t('Title'), description: t('Description'), type: t('Product type', { context: 'shop' }), seo_title: t('Page title'), seo_description: t('Meta description'), excerpt: t('Excerpt'), body: t('Content'), name: t('Name'), bio: t('Bio') };
    const fields = (data.fields || []).filter((f) => data.source?.[f] || saved[f]);
    const value = (f) => draft[lang]?.[f] ?? saved[f] ?? '';
    const change = (f, text) => setDraft((d) => {
        const cur = { ...(d[lang] || {}) };
        if (text === (saved[f] ?? '')) delete cur[f]; else cur[f] = text;
        return { ...d, [lang]: cur };
    });
    const save = async () => {
        setSaving(true);
        try {
            setData(await api.put(`/translations/${type}/${oid}`, { values: draft }));
            setDraft({});
            message.success(t('Saved'));
        } catch (e) { toastErr(e, t('Save failed')); } finally { setSaving(false); }
    };
    const ai = async () => {
        setAiBusy(true);
        try {
            await runAi(siteId, 'translate_catalog', { object_types: [type], object_id: Number(oid), langs: [lang], overwrite: false });
            await load();
            message.success(t('AI finished'));
        } catch (e) { reportAiError(e); } finally { setAiBusy(false); }
    };
    return (
        <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                <Select value={lang} onChange={setLang} style={{ minWidth: 160 }}
                    options={data.languages.map((l) => ({ value: l, label: `${langName(l, i18n?.language)}${Object.keys(draft[l] || {}).length ? ' •' : ''}` }))} />
                <Tooltip title={dirty ? t('Save your changes first') : undefined}>
                    <Button icon={<ThunderboltOutlined />} loading={aiBusy} disabled={dirty} onClick={ai}>{t('Translate all with AI')}</Button>
                </Tooltip>
                <AiCreditBadge />
                <div style={{ flex: 1 }} />
                <Button type={dirty ? 'primary' : 'default'} disabled={!dirty} loading={saving} onClick={save}>{t('Save')}</Button>
            </div>
            {fields.map((f) => (
                <div key={f} data-tr-field={f} style={{ marginBottom: 12 }}>
                    <Text strong style={{ fontSize: 13 }}>{label[f] || f}</Text>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8, marginTop: 4 }}>
                        <div style={{ padding: '4px 8px', fontSize: 13, background: 'var(--ant-color-fill-quaternary, #fafafa)', borderRadius: 6, maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {data.source?.[f] || <Text type="secondary">-</Text>}
                        </div>
                        {LONG.has(f)
                            ? <Input.TextArea value={value(f)} autoSize={{ minRows: 3, maxRows: 12 }} onChange={(e) => change(f, e.target.value)} />
                            : <Input value={value(f)} onChange={(e) => change(f, e.target.value)} />}
                    </div>
                </div>
            ))}
        </>
    );
};

const TranslationsCard = ({ type, oid, style }) => {
    const { t } = useTranslation();
    return <Collapse style={{ marginBottom: 16, ...style }} items={[{ key: 'tr', label: t('Translations'), children: <Body type={type} oid={oid} /> }]} />;
};

export default TranslationsCard;
